import { FrameRange } from 'content-creation-common/models';

import { CHUNK_INDEX_TOKEN } from '../constants';
import { isNumber } from '../../../util/numbers';
import { getThumbnailUrl } from '../../../util/mediaUtils';
import {
  isEmptyString,
  isFunction,
  isNil,
  removeUnusedKeys,
} from '../../../util/object';
import { error } from '../../../util/log';

/**
 * A model to represent a remotely served video. The video can either be
 * a single file or multiple chunked files. It holds references to the VideoChunks
 * that hold the actual bytes or frames from the video.
 */
export default class VideoFile {
  _framesPerChunk = 0;
  _frameRange = null;
  _height = 0;
  _serverId = null;
  _totalFrames = 0;
  _templateId = null;
  _url = null;
  _width = 0;
  _mediaIdentifier = null;

  /*
   * Only being used for local VR environment
   */
  _fetchFrameCallback;

  /**
   * @param {string} mediaIdentifier
   * @param {int} framesPerChunk of the video
   * @param {int} height in pixels, of the video
   * @param {int} totalFrames across all chunks of the video
   * @param {string} url where the video file(s) exist
   * @param {int} width in pixels, of the video
   * @param {string} serverId
   * @param {string} templateId
   */
  constructor(
    mediaIdentifier,
    framesPerChunk,
    height,
    totalFrames,
    url,
    width,
    serverId,
    templateId,
  ) {
    if (
      !isNumber(framesPerChunk) ||
      !isNumber(height) ||
      isNil(mediaIdentifier) ||
      !isNumber(totalFrames) ||
      isEmptyString(url) ||
      !isNumber(width)
    ) {
      throw new Error('Error constructing a VideoFile');
    }
    this._mediaIdentifier = mediaIdentifier;
    this._framesPerChunk = framesPerChunk;
    this._frameRange = new FrameRange(0, totalFrames - 1);
    this._height = height;
    this._totalFrames = totalFrames;
    this._url = url;
    this._width = width;

    if (!isEmptyString(serverId)) {
      this._serverId = serverId;
    }

    if (!isEmptyString(templateId)) {
      this._templateId = templateId;
    }
  }

  equals(videoFile) {
    if (!VideoFile.isVideoFile(videoFile)) return false;
    return (
      this._framesPerChunk === videoFile.framesPerChunk &&
      this._height === videoFile.height &&
      this._serverId === videoFile.serverId &&
      this._templateId === videoFile.templateId &&
      this._totalFrames === videoFile.totalFrames &&
      this._url === videoFile.url &&
      this._width === videoFile.width &&
      this._mediaIdentifier === videoFile.mediaIdentifier
    );
  }

  getFrameRangeOfChunk = (chunkIndex) => {
    const errorMsg = `Trying to get chunk range out of bounds ${chunkIndex}`;
    if (chunkIndex > this.totalChunks || chunkIndex < 0) {
      error(errorMsg);
      return null;
    }
    const startFrameIndex = chunkIndex * this._framesPerChunk;
    if (startFrameIndex >= this.totalFrames) {
      error(errorMsg);
      return null;
    }
    const endFrameIndex = Math.min(
      startFrameIndex + this._framesPerChunk - 1,
      this.totalFrames - 1,
    );
    return new FrameRange(startFrameIndex, endFrameIndex);
  };

  /**
   * Get the total possible number of chunks for the video.
   */
  getTotalChunkCount = () => {
    return Math.ceil(this._totalFrames / this._framesPerChunk);
  };

  /**
   * Get the last chunk index for the video.
   */
  getLastChunkIndex = () => {
    return this.getTotalChunkCount() - 1;
  };

  /**
   * Given a frame index from the total video, determine which chunk
   * index that corresponds to, based on the chunks' size.
   * @param {int} frameIndex relative to the full video
   * @returns {int}
   */
  getChunkIndex = (frameIndex) => {
    if (!this._frameRange.containsFrameNumber(frameIndex)) return -1;
    return Math.floor(frameIndex / this._framesPerChunk);
  };

  /**
   * Contruct the URL to a single chunk of the video.
   * @param {int} chunkIndex
   * @returns {string} a url to the chunk
   */
  getUrlForChunkIndex = (chunkIndex) => {
    return this.url.replace(CHUNK_INDEX_TOKEN, chunkIndex);
  };

  /**
   * Contruct the URL to a single chunk of the video.
   * @param {int} frameNumber
   * @param {object} options
   * @returns {string} url
   */
  getURLByFrameNumber = (frameNumber, options = {}) => {
    if (isFunction(this.fetchFrameCallback)) {
      return this.fetchFrameCallback(frameNumber);
    }

    return getThumbnailUrl(
      this.serverId,
      removeUnusedKeys({
        ...options,
        frameNumber,
        templateId: this.templateId,
      }),
    );
  };

  get fetchFrameCallback() {
    return this._fetchFrameCallback;
  }

  set fetchFrameCallback(value) {
    if (isFunction(value)) {
      this._fetchFrameCallback = value;
    }
  }

  get framesPerChunk() {
    return this._framesPerChunk;
  }

  set framesPerChunk(value) {
    this._framesPerChunk = value;
  }

  get frameRange() {
    return this._frameRange;
  }

  get height() {
    return this._height;
  }

  get templateId() {
    return this._templateId;
  }

  set templateId(value) {
    this._templateId = value;
  }

  get totalFrames() {
    return this._totalFrames;
  }

  get serverId() {
    return this._serverId;
  }

  get totalChunks() {
    return this.getChunkIndex(this.totalFrames - 1) + 1;
  }

  get url() {
    return this._url;
  }

  set url(value) {
    this._url = value;
  }

  get width() {
    return this._width;
  }

  get mediaIdentifier() {
    return this._mediaIdentifier;
  }

  /**
   * @typedef {Object} PlainVideoFile
   * @property {string} _url
   * @property {int} _framesPerChunk
   * @property {int} _height
   * @property {int} _totalFrames
   * @property {int} _width
   */

  /**
   * This exists because only literals can be passed through postMessage calls.
   * Getter/setter functions and nested structs are not preserved.
   * This enables us to fully re-establish the prototype on the other side.
   * @param {PlainVideoFile} source to create a VideoFile
   */
  static fromPlainObject = (source) => {
    /* eslint-disable no-underscore-dangle */
    const newVideoFile = new VideoFile(
      source._mediaIdentifier,
      source._framesPerChunk,
      source._height,
      source._totalFrames,
      source._url,
      source._width,
      source._serverId,
      source._templateId,
    );
    /* eslint-enable no-underscore-dangle */
    return newVideoFile;
  };

  /**
   * Converts a VideoFile into a transferrable object.
   */
  static toPlainObject = (source) => {
    /* eslint-disable no-underscore-dangle */
    const plainObj = {
      _framesPerChunk: source._framesPerChunk,
      _height: source._height,
      _totalFrames: source._totalFrames,
      _url: source._url,
      _width: source._width,
      _mediaIdentifier: source._mediaIdentifier,
    };
    /* eslint-enable no-underscore-dangle */
    return plainObj;
  };

  static isVideoFile(videoFile) {
    return videoFile instanceof VideoFile;
  }
}
